import { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Navigate, useLocation } from 'react-router-dom'
import { setAuthState as setAuthStateAction, signOutUser, challengeMFA, verifyMFA, clearMFAChallenge } from '../store/authSlice'
import { fetchBootstrapData, clearBootstrap } from '../store/bootstrapSlice'
import {
  clearTasks,
  setTasks,
  setProjects,
  setTaskStatuses,
  setBoardColumns,
  setColumnStatusMappings,
  initializeRealtimeSubscriptions,
  cleanupRealtimeSubscriptions
} from '../store/tasksSlice'
import { setSettings } from '../store/userSettingsSlice'
import { setAnnualGoals, setQuarterlyGoals, setWeeklyGoals } from '../store/goalsSlice'
import { setLabels } from '../store/labelsSlice'
import { setSpaces, setPages, clearWiki } from '../store/wikiSlice'
import { fetchLoops, clearLoops } from '../store/loopsSlice'
import { addNotification } from '../store/notificationSlice'
import { clearGoals } from '../store/goalsSlice'
import { clearTaskAttachments } from '../store/taskAttachmentsSlice'
import { fetchDailyCheckIn, clearDailyCheckIn } from '../store/dailyCheckInsSlice'
import { openobserveRum } from '@openobserve/browser-rum'
import { authAPI, mfaAPI, supabase, realtimeAPI, supabaseUrl } from '../utils/supabase'
import { realtimeManager } from '../utils/realtimeManager'
import { refreshManager } from '../utils/refreshManager'
import { getTodayDateString } from '../utils/dateUtils'
import { setRefreshState } from '../store/uiSlice'
import { clearWikiStorage, clearTaskTableStorage } from '../utils/storageUtils'
import { REFRESH_STATE } from '../utils/refreshManager'
import { hideInitialLoader, hideInitialLoaderImmediate, setLoaderMilestone, LOADER_HIDE_DELAY } from '../utils/initialLoaderManager'
import LoadingWrapper from './LoadingWrapper'

/**
 * ProtectedRoute Component
 *
 * Handles authentication, data loading, and workspace initialization for protected pages.
 *
 * LOADING STRATEGY:
 * This component uses direct DOM manipulation to create a seamless loading experience:
 *
 * 1. Initial Load (index.html):
 *    - Inline HTML loader displays within 100ms (before React hydrates)
 *    - Provides instant visual feedback to user
 *    - Theme-aware styling matches app design
 *
 * 2. Data Loading (ProtectedRoute):
 *    - Validates session and fetches workspace data via bootstrap
 *    - Initial loader remains visible during this phase
 *    - No additional spinner needed (prevents double loading indicators)
 *
 * 3. Workspace Ready (lines 89-99):
 *    - Once all data is loaded, hides initial loader with fade transition
 *    - Direct DOM manipulation is necessary here because:
 *      a) The loader exists outside React's root
 *      b) Timing must be precise to avoid flash between loaders
 *      c) AbortController ensures cleanup if user navigates away
 *
 * 4. Navigation Away:
 *    - PublicRoute shows loader again if redirecting to /board
 *    - ProtectedRoute on /board then handles the handoff
 *    - Creates smooth transition without blank screens
 *
 * WHY DIRECT DOM MANIPULATION:
 * - React's declarative model can't control elements outside its root
 * - Prevents flash-of-unstyled-content by showing loader before hydration
 * - Enables sub-100ms feedback (faster than React can mount)
 * - Properly cleaned up with AbortController to prevent leaks
 */

// Bootstrap retry configuration
const BOOTSTRAP_MAX_RETRIES = 2
const BOOTSTRAP_RETRY_DELAY = 1000 // ms

// Token refresh recovery configuration
const MAX_TOKEN_REFRESH_RECOVERY_ATTEMPTS = 3
const TOKEN_REFRESH_HEALTH_CHECK_DELAY = 2000 // Wait 2 seconds for channels to update with new token (accommodates slower connections)


// Bulletproof authentication states - no timing vulnerabilities
const AUTH_STATES = {
  LOADING: 'LOADING',
  UNAUTHENTICATED: 'UNAUTHENTICATED',
  MFA_REQUIRED: 'MFA_REQUIRED',
  AUTHENTICATED: 'AUTHENTICATED'
}

export default function ProtectedRoute({ children }) {
  const dispatch = useDispatch()
  const { mfaChallenge } = useSelector((state) => state.auth)
  const location = useLocation()

  // Get user settings - must be called at top level (Rules of Hooks)
  const userSettings = useSelector((state) => state.userSettings.settings)

  // Monitor when workspace is truly ready (Board data loaded)
  const tasksInitialized = useSelector((state) => state.tasks.initialized)
  const isWorkspaceDataReady = tasksInitialized.tasks && tasksInitialized.projects &&
                                tasksInitialized.taskStatuses && tasksInitialized.boardColumns &&
                                tasksInitialized.columnStatusMappings

  // Bulletproof state machine - single source of truth
  const [authState, setAuthState] = useState(AUTH_STATES.LOADING)
  const [validatedSession, setValidatedSession] = useState(null)
  const [isValidating, setIsValidating] = useState(false)
  const [isBootstrapping, setIsBootstrapping] = useState(false)
  const [showLoadingOverlay, setShowLoadingOverlay] = useState(true)
  const [workspaceReady, setWorkspaceReady] = useState(false)

  // MFA verification state
  const [mfaVerificationCode, setMfaVerificationCode] = useState('')
  const [mfaLoading, setMfaLoading] = useState(false)
  const [mfaError, setMfaError] = useState(null)

  // Password recovery redirect state
  const [redirectToPasswordReset, setRedirectToPasswordReset] = useState(false)

  // Token refresh recovery tracking (prevent unbounded retries)
  const [tokenRefreshRecoveryAttempts, setTokenRefreshRecoveryAttempts] = useState(0)

  // Mounted ref to prevent state updates after unmount
  const isMountedRef = useRef(true)

  // Trigger workspace ready state when data is loaded
  useEffect(() => {
    if (!isBootstrapping && isWorkspaceDataReady && !workspaceReady) {
      // Give React one more frame to render the actual Board content
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          // Update loader progress to 100%
          setLoaderMilestone('WORKSPACE_READY')
          setWorkspaceReady(true)
          setShowLoadingOverlay(false)
        })
      })
    }
  }, [isBootstrapping, isWorkspaceDataReady, workspaceReady])

  // Hide the initial loader after workspace is ready (separate effect to avoid cleanup race)
  useEffect(() => {
    if (workspaceReady) {
      // Small delay to let app content start fading in before loader fades out
      // This prevents a blank flash on slower connections
      const delayTimeoutId = setTimeout(() => {
        hideInitialLoader()
      }, LOADER_HIDE_DELAY)

      return () => clearTimeout(delayTimeoutId)
    }
  }, [workspaceReady])

  // Session validation with timeout and enhanced error handling
  useEffect(() => {
    // Prevent duplicate executions (React 18 StrictMode protection)
    if (isValidating) {
      return
    }

    const abortController = new AbortController()
    
    const validateSessionWithTimeout = async (timeoutMs = 10000) => {
      return Promise.race([
        // Actual session validation
        authAPI.getSession(),
        // Timeout fallback
        new Promise((_, reject) => {
          const timeout = setTimeout(() => {
            reject(new Error('Session validation timeout'))
          }, timeoutMs)
          
          abortController.signal?.addEventListener('abort', () => {
            clearTimeout(timeout)
            reject(new Error('Validation aborted'))
          })
        })
      ])
    }
    
    const validateSessionSynchronously = async () => {
      try {
        if (abortController.signal.aborted) return
        
        setIsValidating(true)

        // Step 1: Get current session with timeout protection
        const { session, error: sessionError } = await validateSessionWithTimeout()
        if (abortController.signal.aborted) return

        if (sessionError || !session) {
          setAuthState(AUTH_STATES.UNAUTHENTICATED)
          return
        }

        // Check session staleness - if token is near expiry, log warning
        if (session.expires_at) {
          const expiresAt = session.expires_at
          const now = Math.floor(Date.now() / 1000)
          const timeToExpiry = expiresAt - now
          
          if (timeToExpiry < 300) { // Less than 5 minutes
            console.warn('[ProtectedRoute] Session near expiry in', timeToExpiry, 'seconds')
          }
        }

        // Step 2: Check AAL requirements with timeout protection
        let aalData, aalError
        try {
          const aalResult = await Promise.race([
            supabase.auth.mfa.getAuthenticatorAssuranceLevel(),
            new Promise((_, reject) => {
              const timeout = setTimeout(() => {
                reject(new Error('AAL check timeout'))
              }, 5000) // Shorter timeout for AAL check
              
              abortController.signal?.addEventListener('abort', () => {
                clearTimeout(timeout)
                reject(new Error('AAL check aborted'))
              })
            })
          ])
          aalData = aalResult.data
          aalError = aalResult.error
        } catch (timeoutError) {
          console.warn('[ProtectedRoute] AAL check failed/timeout, assuming no MFA required:', timeoutError.message)
          aalData = { currentLevel: 'aal1', nextLevel: null }
          aalError = null
        }

        if (abortController.signal.aborted) return

        if (aalError) {
          console.error('[ProtectedRoute] AAL check error:', aalError)
          // Fail secure - if we can't check AAL, assume authentication needed
          setAuthState(AUTH_STATES.UNAUTHENTICATED)
          return
        }

        // Step 3: Synchronously determine final authentication state
        if (aalData?.nextLevel === 'aal2' && aalData?.currentLevel !== 'aal2') {
          setValidatedSession(session) // Store for post-MFA use
          setAuthState(AUTH_STATES.MFA_REQUIRED)

          // MFA challenge will be handled in the MFA screen component
          // No need to dispatch challengeMFA here
        } else {
          setValidatedSession(session)
          setAuthState(AUTH_STATES.AUTHENTICATED)

          // Update loader progress - session validated
          setLoaderMilestone('SESSION_VALIDATED')

          // Set Redux auth state
          dispatch(setAuthStateAction({ session, user: session.user }))

          // Set OpenObserve RUM user context for session grouping (privacy-safe, no PII)
          openobserveRum.setUser({ id: session.user.id })

          // Load data after state is set to avoid Redux timing issues
          setTimeout(() => {
            if (!abortController.signal.aborted) {
              loadGlobalData(session.user.id, abortController.signal).catch(error => {
                if (!abortController.signal.aborted) {
                  console.error('[ProtectedRoute] Error loading data:', error)
                }
              })
            }
          }, 0)
        }
      } catch (error) {
        if (!abortController.signal.aborted) {
          console.error('[ProtectedRoute] Session validation failed:', error)
          // Fail secure - any error means unauthenticated
          setAuthState(AUTH_STATES.UNAUTHENTICATED)
        }
      } finally {
        if (!abortController.signal.aborted) {
          setIsValidating(false)
        }
      }
    }

    validateSessionSynchronously()
    
    // Cleanup function to abort ongoing operations
    return () => {
      abortController.abort()
      setIsValidating(false)
    }
  }, [dispatch])

  // Global data loading function with abort signal support - now uses unified bootstrap
  const loadGlobalData = async (userId, abortSignal = null, retryCount = 0) => {
    try {
      if (abortSignal?.aborted) return

      // Set bootstrapping state to true at the start
      setIsBootstrapping(true)

      // Update loader progress - bootstrap starting
      setLoaderMilestone('BOOTSTRAP_STARTED')

      // Load all data in a single optimized query
      const bootstrapResult = await dispatch(fetchBootstrapData(userId))

      if (abortSignal?.aborted) return

      if (bootstrapResult.type === 'bootstrap/fetch/fulfilled') {
        const bootstrapData = bootstrapResult.payload

        // Dispatch data to individual slices
        const {
          userSettings,
          tasks,
          projects,
          taskStatuses,
          boardColumns,
          columnStatusMappings,
          annualGoals,
          quarterlyGoals,
          weeklyGoals,
          taskLabels,
          wikiSpaces,
          wikiPages
        } = bootstrapData

        // Set user settings in Redux
        if (userSettings) {
          dispatch(setSettings(userSettings))
        }

        // Set tasks data in Redux
        dispatch(setTasks(tasks))
        dispatch(setProjects(projects))
        dispatch(setTaskStatuses(taskStatuses))
        dispatch(setBoardColumns(boardColumns))
        dispatch(setColumnStatusMappings(columnStatusMappings))

        // Set labels data in Redux
        dispatch(setLabels(taskLabels))

        // Set goals data in Redux
        dispatch(setAnnualGoals(annualGoals))
        dispatch(setQuarterlyGoals(quarterlyGoals))
        dispatch(setWeeklyGoals(weeklyGoals))

        // Set wiki data in Redux
        dispatch(setSpaces(wikiSpaces))
        dispatch(setPages(wikiPages))

        // Load daily check-in separately (timezone-dependent, needs today's date)
        if (!abortSignal?.aborted && userSettings?.timezone) {
          try {
            const today = getTodayDateString(userSettings.timezone)
            await dispatch(fetchDailyCheckIn({ userId, date: today }))
          } catch (error) {
            if (!abortSignal?.aborted) {
              console.error('[ProtectedRoute] Error loading daily check-in:', error)
            }
          }
        }

        // Load loops data (not included in bootstrap)
        if (!abortSignal?.aborted) {
          try {
            await dispatch(fetchLoops(userId))
          } catch (error) {
            if (!abortSignal?.aborted) {
              console.error('[ProtectedRoute] Error loading loops:', error)
            }
          }
        }

        // Initialize realtime subscriptions if enabled by user
        const realtimeEnabled = userSettings?.realtime_enabled ?? false
        if (realtimeEnabled && !abortSignal?.aborted) {
          try {
            if (initializeRealtimeSubscriptions && typeof initializeRealtimeSubscriptions === 'function') {
              // CRITICAL FIX: Pass forceFresh: true to ensure clean initialization on page reload
              // This prevents stale localStorage metadata from causing connection failures
              await dispatch(initializeRealtimeSubscriptions({ userId, forceFresh: true }))
            }
          } catch (error) {
            if (!abortSignal?.aborted) {
              console.error('[ProtectedRoute] Error initializing realtime:', error)
            }
          }
        }

        // Update loader progress - bootstrap complete
        if (!abortSignal?.aborted) {
          setLoaderMilestone('BOOTSTRAP_COMPLETE')
        }
      } else {
        console.error('[ProtectedRoute] Bootstrap fetch failed:', bootstrapResult.payload)

        // Retry logic with exponential backoff
        if (retryCount < BOOTSTRAP_MAX_RETRIES && !abortSignal?.aborted) {
          const retryDelay = BOOTSTRAP_RETRY_DELAY * Math.pow(2, retryCount) // Exponential backoff

          await new Promise(resolve => setTimeout(resolve, retryDelay))

          if (!abortSignal?.aborted) {
            return loadGlobalData(userId, abortSignal, retryCount + 1)
          }
        } else {
          // All retries exhausted - show user-facing error
          console.error('[ProtectedRoute] Failed to load initial app data after retries.')

          dispatch(addNotification({
            id: `bootstrap-error-${Date.now()}`,
            type: 'error',
            message: 'Failed to load app data. Please refresh the page or try again later.'
          }))
        }
      }
    } catch (error) {
      if (!abortSignal?.aborted) {
        console.error('[ProtectedRoute] Error in loadGlobalData:', error)
      }
      // Don't throw error - allow app to load even if data loading fails
    } finally {
      // Always clear bootstrapping state when done
      // Fade is now triggered by the useEffect watching isWorkspaceDataReady
      if (!abortSignal?.aborted) {
        setIsBootstrapping(false)
      }
    }
  }

  // MFA completion is now handled directly in handleMFAVerification
  // No automatic transitions based on Redux MFA state to prevent bypass

  // Listen for auth state changes (sign out handling + token refresh + password recovery)
  useEffect(() => {
    const { data: { subscription } } = authAPI.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        // User arrived via password reset email link
        // Redirect them to the reset password page to complete the flow
        console.log('[ProtectedRoute] PASSWORD_RECOVERY event detected, redirecting to /reset-password')
        setRedirectToPasswordReset(true)
        return
      }
      if (event === 'SIGNED_OUT') {
        setAuthState(AUTH_STATES.UNAUTHENTICATED)
        setValidatedSession(null)
        setShowLoadingOverlay(true) // Reset overlay for next login
        setWorkspaceReady(false) // Reset workspace ready state
        dispatch(setAuthStateAction({ session: null, user: null }))
        openobserveRum.clearUser() // Clear RUM user context on logout
        dispatch(cleanupRealtimeSubscriptions())
        dispatch(clearTasks())
        dispatch(clearGoals())
        dispatch(clearDailyCheckIn())
        dispatch(clearLoops())
        dispatch(clearBootstrap())
        dispatch(clearTaskAttachments())
        dispatch(clearWiki())
        clearWikiStorage()
        clearTaskTableStorage()
      } else if (event === 'TOKEN_REFRESHED' && session?.access_token) {
        // Reset recovery attempts on successful token refresh
        // This prevents accumulation of attempts across multiple refresh cycles
        if (tokenRefreshRecoveryAttempts > 0) {
          setTokenRefreshRecoveryAttempts(0)
          console.log('[ProtectedRoute] Token refreshed successfully, recovery attempts reset')
        }

        // Check if realtime is active (has subscriptions)
        if (realtimeManager.subscriptions.size > 0) {
          // CRITICAL FIX: Update realtime auth when token refreshes
          // This ensures realtime channels stay authenticated as tokens expire
          console.log('[ProtectedRoute] Token refreshed, updating realtime auth')
          realtimeAPI.setAuth(session.access_token)

          // CRITICAL FIX: Validate channel health after token refresh to catch any
          // channels that died during the token transition window
          setTimeout(() => {
            // Check if component still mounted and user still authenticated
            if (!validatedSession) {
              console.log('[ProtectedRoute] Session invalid during token refresh recovery, skipping')
              return
            }

            const isHealthy = realtimeManager.validateConnectionHealth()
            if (!isHealthy) {
              // Check if we've exceeded max recovery attempts
              if (tokenRefreshRecoveryAttempts >= MAX_TOKEN_REFRESH_RECOVERY_ATTEMPTS) {
                console.error('[ProtectedRoute] Max token refresh recovery attempts reached. Please refresh the page.')
                dispatch(addNotification({
                  id: `realtime-recovery-failed-${Date.now()}`,
                  type: 'error',
                  message: 'Realtime connection lost. Please refresh the page to reconnect.'
                }))
                return
              }

              console.warn(`[ProtectedRoute] Some channels unhealthy after token refresh, attempting recovery (${tokenRefreshRecoveryAttempts + 1}/${MAX_TOKEN_REFRESH_RECOVERY_ATTEMPTS})`)
              setTokenRefreshRecoveryAttempts(prev => prev + 1)

              // Attempt to reinitialize if health check fails after token refresh
              dispatch(cleanupRealtimeSubscriptions())
                .then(() => {
                  console.log('[ProtectedRoute] Reinitializing realtime after failed token refresh health check')
                  return dispatch(initializeRealtimeSubscriptions({ userId: session.user.id, forceFresh: true }))
                })
                .then(() => {
                  // Reset recovery attempts on successful recovery
                  setTokenRefreshRecoveryAttempts(0)
                  console.log('[ProtectedRoute] Realtime recovery successful, attempts reset')
                })
                .catch(error => {
                  console.error('[ProtectedRoute] Failed to recover realtime after token refresh:', error)
                })
            } else {
              console.log('[ProtectedRoute] All channels healthy after token refresh')
              // Reset recovery attempts when health check confirms stability
              if (tokenRefreshRecoveryAttempts > 0) {
                setTokenRefreshRecoveryAttempts(0)
                console.log('[ProtectedRoute] Health check passed, recovery attempts reset')
              }
            }
          }, TOKEN_REFRESH_HEALTH_CHECK_DELAY)
        }
      }
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [dispatch, tokenRefreshRecoveryAttempts])

  // Reconnection refresh handler - refreshes data when connection is restored after disconnect
  // Uses the refreshManager state machine for network probing, debouncing, and retry logic
  useEffect(() => {
    // Reset mounted ref on each effect run (important when dependencies change)
    isMountedRef.current = true

    // Only set up reconnection handling if we have an authenticated session
    if (!validatedSession?.user?.id) {
      return
    }

    const userId = validatedSession.user.id

    // Error handler for failed refresh - shows user notification
    const handleRefreshError = () => {
      if (isMountedRef.current) {
        dispatch(addNotification({
          id: `refresh-error-${Date.now()}`,
          type: 'error',
          message: 'Failed to sync data. Please refresh the page if issues persist.',
          duration: 8000
        }))
      }
    }

    // Simple refresh callback - just fetches and dispatches data
    // All retry/error handling is done by refreshManager
    const performRefresh = async () => {
      const result = await dispatch(fetchBootstrapData(userId))

      if (result.type !== 'bootstrap/fetch/fulfilled') {
        // Throw to trigger refreshManager's retry logic
        throw new Error(result.payload || 'Bootstrap refresh failed')
      }

      // Update all Redux slices with fresh data
      const {
        userSettings: freshUserSettings,
        tasks,
        projects,
        taskStatuses,
        boardColumns,
        columnStatusMappings,
        annualGoals,
        quarterlyGoals,
        weeklyGoals,
        taskLabels,
        wikiSpaces: freshWikiSpaces,
        wikiPages: freshWikiPages
      } = result.payload

      if (freshUserSettings) {
        dispatch(setSettings(freshUserSettings))
      }
      dispatch(setTasks(tasks))
      dispatch(setProjects(projects))
      dispatch(setTaskStatuses(taskStatuses))
      dispatch(setBoardColumns(boardColumns))
      dispatch(setColumnStatusMappings(columnStatusMappings))
      dispatch(setLabels(taskLabels))
      dispatch(setAnnualGoals(annualGoals))
      dispatch(setQuarterlyGoals(quarterlyGoals))
      dispatch(setWeeklyGoals(weeklyGoals))
      dispatch(setSpaces(freshWikiSpaces))
      dispatch(setPages(freshWikiPages))

      // Refresh daily check-in if we have timezone
      const timezone = freshUserSettings?.timezone || userSettings?.timezone
      if (timezone) {
        const today = getTodayDateString(timezone)
        try {
          await dispatch(fetchDailyCheckIn({ userId, date: today }))
        } catch (err) {
          // Log but don't fail - daily check-in is non-critical during reconnection
          console.log('[ProtectedRoute] Daily check-in refresh failed (non-critical):', err?.message)
        }
      }

      console.log('[ProtectedRoute] Data refresh completed successfully')
    }

    // Subscribe to refreshManager state changes for UI updates
    // Guard with isMountedRef to prevent dispatch after unmount
    const unsubscribeRefreshState = refreshManager.subscribe((state) => {
      if (isMountedRef.current) {
        dispatch(setRefreshState(state))
      }
    })

    // Visibility change handler - detect when tab regains focus after sleep
    // refreshManager handles: network probing, debouncing, retries, cooldown
    // and only refreshes if page was hidden 10+ seconds (wake-from-sleep detection)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        // Track when page becomes hidden for wake-from-sleep detection
        refreshManager.onVisibilityHidden()
      } else {
        // Page became visible - request refresh
        // refreshManager will skip if hidden < 10s (not wake-from-sleep)
        refreshManager.requestRefresh(performRefresh, {
          reason: 'visibility-change',
          supabaseUrl,
          onError: handleRefreshError
        })
      }
    }

    // Network online event listener
    const handleOnline = () => {
      console.log('[ProtectedRoute] Network online event detected')
      refreshManager.requestRefresh(performRefresh, {
        reason: 'network-online',
        supabaseUrl,
        onError: handleRefreshError
      })
    }

    // Subscribe to realtime connection state changes
    const unsubscribeRealtime = realtimeManager.onConnectionStateChange((isConnected, metadata) => {
      if (isConnected && metadata.wasDisconnected && metadata.disconnectDuration) {
        console.log(`[ProtectedRoute] Reconnected after ${(metadata.disconnectDuration / 1000).toFixed(1)}s`)
        refreshManager.requestRefresh(performRefresh, {
          reason: 'realtime-reconnection',
          supabaseUrl,
          onError: handleRefreshError
        })
      }
    })

    document.addEventListener('visibilitychange', handleVisibilityChange)
    window.addEventListener('online', handleOnline)

    // Cleanup
    return () => {
      isMountedRef.current = false
      unsubscribeRefreshState()
      unsubscribeRealtime()
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      window.removeEventListener('online', handleOnline)
      refreshManager.cancel()
    }
  }, [dispatch, validatedSession, userSettings])

  // MFA verification handlers
  const handleMFAVerification = async () => {
    if (!mfaVerificationCode || mfaVerificationCode.length !== 6) {
      setMfaError('Please enter a 6-digit verification code')
      return
    }

    setMfaLoading(true)
    setMfaError(null)

    try {
      // Get MFA factors and create challenge if not already done
      const { factors } = await mfaAPI.getVerifiedFactors()
      if (factors.length === 0) {
        throw new Error('No MFA factors available')
      }

      const factor = factors[0]
      
      // Create MFA challenge
      const { data: challengeData, error: challengeError } = await supabase.auth.mfa.challenge({
        factorId: factor.id
      })
      
      if (challengeError) throw challengeError
      
      // Verify challenge
      const { data: verifyData, error: verifyError } = await supabase.auth.mfa.verify({
        factorId: factor.id,
        challengeId: challengeData.id,
        code: mfaVerificationCode
      })
      
      if (verifyError) throw verifyError
      
      setMfaVerificationCode('')
      
      // Get updated session after MFA verification
      const { session } = await authAPI.getSession()
      if (session) {
        setValidatedSession(session)
        
        // Clear the MFA challenge in Redux
        dispatch(clearMFAChallenge())
        
        // Transition to authenticated state
        setAuthState(AUTH_STATES.AUTHENTICATED)
        dispatch(setAuthStateAction({ session, user: session.user }))
        
        // Load global data
        loadGlobalData(session.user.id).catch(error => {
          console.error('[ProtectedRoute] Error loading data after MFA:', error)
        })
      }
    } catch (error) {
      setMfaError(error.message || 'Verification failed. Please try again.')
    } finally {
      setMfaLoading(false)
    }
  }

  const handleMFACancel = () => {
    dispatch(clearMFAChallenge())
    dispatch(signOutUser())
    setAuthState(AUTH_STATES.UNAUTHENTICATED)
  }

  // BULLETPROOF RENDER LOGIC - No timing vulnerabilities
  // The authentication state determines everything synchronously

  // Handle password recovery redirect (user clicked reset link in email)
  if (redirectToPasswordReset) {
    hideInitialLoaderImmediate()
    return <Navigate to="/reset-password" replace />
  }

  // Show loading screen ONLY during initial auth - not during bootstrap
  // The initial HTML loader handles this, so just return null to avoid double spinner
  if (authState === AUTH_STATES.LOADING) {
    return null
  }

  if (authState === AUTH_STATES.UNAUTHENTICATED) {
    // Hide initial loader before navigating to login
    hideInitialLoaderImmediate()
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (authState === AUTH_STATES.MFA_REQUIRED) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background-secondary px-4">
        <div className="w-full max-w-sm">
          <div className="apple-card px-6 py-8">
            <div className="text-center mb-6">
              <div className="w-12 h-12 bg-status-info rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="apple-text-title text-xl">Multi-Factor Authentication Required</h2>
              <p className="apple-text-body text-text-muted mt-2">
                Please verify your identity with your authenticator app
              </p>
            </div>

            <div className="space-y-6">
              <div>
                <label htmlFor="mfa-code" className="block text-sm font-medium text-label-secondary mb-2">
                  Verification Code
                </label>
                <input
                  id="mfa-code"
                  type="text"
                  maxLength={6}
                  placeholder="000000"
                  className="apple-input text-center font-mono text-lg"
                  value={mfaVerificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    setMfaVerificationCode(value)
                    if (mfaError) setMfaError(null)
                  }}
                  autoComplete="one-time-code"
                  autoFocus
                  disabled={mfaLoading}
                />
                {mfaError && (
                  <p className="text-sm text-status-danger mt-2">{mfaError}</p>
                )}
              </div>

              <div className="space-y-3">
                <button
                  onClick={handleMFAVerification}
                  disabled={mfaLoading || mfaVerificationCode.length !== 6}
                  className="apple-button-primary w-full disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {mfaLoading ? 'Verifying...' : 'Verify'}
                </button>
                <button
                  onClick={handleMFACancel}
                  className="apple-button-secondary w-full"
                  disabled={mfaLoading}
                >
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (authState === AUTH_STATES.AUTHENTICATED) {
    // Wait for user settings to load before making routing decisions
    if (!userSettings) {
      // Rely on loading overlay already rendered below
      return null
    }

    // Check if user needs onboarding
    const isOnboardingPage = location.pathname === '/onboarding'

    // If onboarding not completed and not already on onboarding page, redirect to onboarding
    if (!userSettings.onboarding_completed && !isOnboardingPage) {
      return <Navigate to="/onboarding" replace />
    }

    // If onboarding completed but trying to access onboarding page, redirect to board
    if (userSettings.onboarding_completed && isOnboardingPage) {
      return <Navigate to="/board" replace />
    }

    // Render workspace with fade-in transition
    // The initial HTML loader remains visible until workspace is ready
    // No separate overlay needed - we reuse the initial loader for a seamless experience
    return (
      <div
        className={`transition-opacity duration-700 ease-in-out ${
          workspaceReady ? 'opacity-100' : 'opacity-0'
        }`}
      >
        {children}
      </div>
    )
  }

  // Fallback - fail secure
  console.error('[ProtectedRoute] Invalid auth state:', authState)
  return <Navigate to="/login" state={{ from: location }} replace />
}
